// room-model -- API client wrappers + view-model mappers for the room IA
// (The Brief + The Spec). Mirrors desk-model.js conventions.
import { api } from './api.js';
import { streamPost } from './sse.js';

/* ---------- The Brief ---------- */
export function fetchBrief(projectId) {
  return api('/api/projects/' + projectId + '/brief');
}
export function fetchPending(projectId) {
  return api('/api/projects/' + projectId + '/brief/pending');
}
export function approveEntry(projectId, entryId, closesAskId) {
  const body = closesAskId ? { closesAskId } : {};
  return api('/api/projects/' + projectId + '/brief/entries/' + entryId + '/approve', { method: 'POST', body });
}
export function rejectEntry(projectId, entryId, note) {
  return api('/api/projects/' + projectId + '/brief/entries/' + entryId + '/reject', { method: 'POST', body: { note: note || '' } });
}
export function reviseEntry(projectId, entryId, { prose, note }) {
  return api('/api/projects/' + projectId + '/brief/entries/' + entryId + '/revise', { method: 'POST', body: { prose, note: note || '' } });
}

/* ---------- The Spec ---------- */
export function fetchSpec(projectId) {
  return api('/api/projects/' + projectId + '/spec');
}
export function specReadiness(projectId) {
  return api('/api/projects/' + projectId + '/spec/readiness');
}
export function drawSpec(projectId) {
  return api('/api/projects/' + projectId + '/spec/draw', { method: 'POST' });
}

/* ---------- view-model mappers ---------- */

// toRoomCard -- a canon entry in the Experience Map section becomes a room card.
// Structured payload (name/kind/whatHappens) is preferred; prose is the fallback.
export function toRoomCard(entry) {
  const p = entry.payload || {};
  return {
    id: entry._id,
    agent: entry.agent,
    name: p.name || null,
    kind: p.kind || null,
    body: (p.whatHappens || entry.prose || '').trim(),
  };
}

export const EXPERIENCE_MAP = 'experience-map';

/* ---------- Roundtables ---------- */
export function fetchRoundtables(projectId) {
  return api('/api/projects/' + projectId + '/roundtables');
}
export function fetchRoundtable(projectId, rtId) {
  return api('/api/projects/' + projectId + '/roundtables/' + rtId);
}
export function convene(projectId, { topic, participants, goal, roundCap }) {
  return api('/api/projects/' + projectId + '/roundtables', { method: 'POST', body: { topic, participants, goal, roundCap } });
}
export function conveneStream(projectId, { topic, participants, goal, roundCap }, onEvent) {
  return streamPost('/api/projects/' + projectId + '/roundtables/stream', { topic, participants, goal, roundCap }, onEvent);
}

/* ---------- Disputes ---------- */
export function fetchDisputes(projectId) {
  return api('/api/projects/' + projectId + '/disputes');
}
export function ruleDispute(projectId, disputeId, outcome, note) {
  return api('/api/projects/' + projectId + '/disputes/' + disputeId + '/rule', { method: 'POST', body: { outcome, note: note || '' } });
}

/* ---------- Away-work ---------- */
export function fetchAwayWork(projectId) {
  return api('/api/projects/' + projectId + '/away-work');
}
export function setAwayWork(projectId, enabled) {
  return api('/api/projects/' + projectId + '/away-work', { method: 'PATCH', body: { enabled } });
}
export function runAwayWork(projectId) {
  return api('/api/projects/' + projectId + '/away-work/run', { method: 'POST' });
}

/* ---------- The Showrunner / roster ---------- */
export function fetchRoster(projectId) {
  return api('/api/projects/' + projectId + '/roster');
}
export function activateAgent(projectId, agent, { mandate, brief }) {
  return api('/api/projects/' + projectId + '/roster/' + agent + '/activate', { method: 'POST', body: { mandate: mandate || '', brief: brief || '' } });
}
export function pauseAgent(projectId, agent) {
  return api('/api/projects/' + projectId + '/roster/' + agent + '/pause', { method: 'POST' });
}
export function fetchRecommendation(projectId) {
  return api('/api/projects/' + projectId + '/recommendations');
}
export function nextRecommendation(projectId) {
  return api('/api/projects/' + projectId + '/recommendations/next', { method: 'POST' });
}
export function dismissRecommendation(projectId, recId, note) {
  return api('/api/projects/' + projectId + '/recommendations/' + recId + '/dismiss', { method: 'POST', body: { note: note || '' } });
}
export function declineAgent(projectId, agent, note) {
  return api('/api/projects/' + projectId + '/roster/' + agent + '/decline', { method: 'POST', body: { note: note || '' } });
}

/* ---------- Autopilot ---------- */
export function getAutopilot(projectId) {
  return api('/api/projects/' + projectId + '/autopilot');
}
export function setAutopilot(projectId, enabled) {
  return api('/api/projects/' + projectId + '/autopilot', { method: 'PATCH', body: { enabled } });
}
export function tickAutopilot(projectId) {
  return api('/api/projects/' + projectId + '/autopilot/tick', { method: 'POST' });
}
